import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuthStore, LanguageProvider, kycApi } from '@eshop/shared'
import { Login } from './pages/auth/Login'
import { Register } from './pages/auth/Register'
import { DashboardPage } from './pages/backoffice/DashboardPage'
import { ProductsPage } from './pages/backoffice/ProductsPage'
import { OrdersPage } from './pages/backoffice/OrdersPage'
import { CategoriesPage } from './pages/backoffice/CategoriesPage'
import { KYCIntro } from './pages/kyc/KYCIntro'
import { KYCWizard } from './pages/kyc/KYCWizard'
import { KYCPending } from './pages/kyc/KYCPending'
import { SellerLandingPage } from './pages/SellerLandingPage'
import './styles/globals.css'

const queryClient = new QueryClient()
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID

if (!GOOGLE_CLIENT_ID) {
  console.error('❌ ERROR: VITE_GOOGLE_CLIENT_ID is not configured!')
  console.error('Make sure .env has: VITE_GOOGLE_CLIENT_ID=...')
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore()
  const [kycStatus, setKycStatus] = useState<any>(null)
  const [kycLoading, setKycLoading] = useState(true)

  useEffect(() => {
    const checkKYC = async () => {
      if (isAuthenticated && !user?.is_staff) {
        try {
          const status = await kycApi.checkStatus()
          setKycStatus(status)
        } catch (err) {
          console.error('Error checking KYC:', err)
        } finally {
          setKycLoading(false)
        }
      } else {
        setKycLoading(false)
      }
    }
    checkKYC()
  }, [isAuthenticated, user])

  if (isLoading || kycLoading) {
    return <div>Loading...</div>
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Staff users don't need KYC
  if (user?.is_staff) {
    return <>{children}</>
  }

  // Check KYC status for regular vendors
  if (kycStatus) {
    if (kycStatus.status === 'pending') {
      return <Navigate to="/kyc/pending" replace />
    }
    if (kycStatus.kyc_required && !kycStatus.is_approved) {
      return <Navigate to="/kyc" replace />
    }
  }

  return <>{children}</>
}

function App() {
  const { checkAuth } = useAuthStore()

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<SellerLandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/kyc" element={<KYCIntro />} />
              <Route path="/kyc/wizard" element={<KYCWizard />} />
              <Route path="/kyc/pending" element={<KYCPending />} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
              <Route path="/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </LanguageProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  )
}

export default App
